import type { RefObject } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  IconButton,
  Tooltip,
  Flex,
  Input,
  useColorMode,
} from '@chakra-ui/react'
import { FileEdit, Share2, X } from 'lucide-react'
import type { Draft } from '@/lib/storage/local-storage'

interface DraftsListProps {
  drafts: Draft[]
  currentDraftId: string | null
  selectedFileId?: string
  onSelectDraft: (draftId: string) => void
  onDeleteDraft: (draftId: string) => void
  onShareDraft?: () => void
  // Rename state from useDraftRename
  renamingDraftId: string | null
  renameValue: string
  setRenameValue: (value: string) => void
  renameInputRef: RefObject<HTMLInputElement | null>
  handleStartRename: (draft: Draft) => void
  handleConfirmRename: () => void
  handleRenameKeyDown: (e: React.KeyboardEvent) => void
  // Layout
  draftsHeight: number
  minSectionHeight: number
  maxSectionHeight: number
  draftsSectionRef: RefObject<HTMLDivElement | null>
  hasActiveShares: boolean
}

export function DraftsList({
  drafts,
  currentDraftId,
  selectedFileId,
  onSelectDraft,
  onDeleteDraft,
  onShareDraft,
  renamingDraftId,
  renameValue,
  setRenameValue,
  renameInputRef,
  handleStartRename,
  handleConfirmRename,
  handleRenameKeyDown,
  draftsHeight,
  minSectionHeight,
  maxSectionHeight,
  draftsSectionRef,
  hasActiveShares,
}: DraftsListProps) {
  const { colorMode } = useColorMode()

  if (drafts.length === 0) return null

  return (
    <Box
      ref={draftsSectionRef}
      flexShrink={0}
      {...(hasActiveShares
        ? {
            h: `${draftsHeight}px`,
            minH: `${minSectionHeight}px`,
            maxH: `${maxSectionHeight}px`,
          }
        : {
            flex: 1,
            minH: 0,
          })}
      display="flex"
      flexDirection="column"
    >
      <Flex
        align="center"
        justify="space-between"
        px={3}
        py={2}
        flexShrink={0}
      >
        <Text
          fontSize="xs"
          fontWeight="semibold"
          textTransform="uppercase"
          color="stone.500"
        >
          Drafts ({drafts.length})
        </Text>
      </Flex>

      {/* Drafts list */}
      <VStack
        spacing={0}
        align="stretch"
        px={2}
        pt={1}
        pb={2}
        overflow="auto"
        flex={1}
        minH={0}
      >
        {drafts.map((draft) => (
          <HStack
            key={draft.id}
            px={2}
            py={1.5}
            borderRadius="md"
            cursor="pointer"
            onClick={() => {
              if (renamingDraftId !== draft.id) {
                onSelectDraft(draft.id)
              }
            }}
            onDoubleClick={() => handleStartRename(draft)}
            bg={
              currentDraftId === draft.id && !selectedFileId
                ? colorMode === 'dark'
                  ? 'stone.700'
                  : 'stone.200'
                : 'transparent'
            }
            _hover={{
              bg: colorMode === 'dark' ? 'stone.700' : 'stone.200',
            }}
            transition="all 0.15s ease"
            role="group"
            position="relative"
          >
            {/* Delete button - top left corner, shown on hover */}
            {renamingDraftId !== draft.id && (
              <Box
                position="absolute"
                top="-1px"
                left="-1px"
                opacity={0}
                _groupHover={{ opacity: 1 }}
                transition="opacity 0.15s ease"
                zIndex={1}
              >
                <Tooltip label="Delete" placement="top">
                  <Box
                    as="button"
                    w="14px"
                    h="14px"
                    borderRadius="full"
                    bg={colorMode === 'dark' ? 'stone.500' : 'stone.400'}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    _hover={{
                      bg: colorMode === 'dark' ? 'stone.400' : 'stone.500',
                    }}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation()
                      onDeleteDraft(draft.id)
                    }}
                  >
                    <Icon
                      as={X}
                      boxSize={2}
                      color="white"
                      strokeWidth={3}
                    />
                  </Box>
                </Tooltip>
              </Box>
            )}
            <Icon
              as={FileEdit}
              boxSize={4}
              color={colorMode === 'dark' ? 'stone.400' : 'stone.500'}
              flexShrink={0}
            />
            {renamingDraftId === draft.id ? (
              <Input
                ref={renameInputRef}
                flex={1}
                size="xs"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={handleConfirmRename}
                onKeyDown={handleRenameKeyDown}
                onClick={(e) => e.stopPropagation()}
                bg={colorMode === 'dark' ? 'stone.900' : 'white'}
                borderColor={colorMode === 'dark' ? 'stone.600' : 'stone.300'}
                _focus={{
                  borderColor: 'brand.500',
                  boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
                }}
              />
            ) : (
              <Tooltip
                label="Double-click to rename"
                placement="right"
                openDelay={800}
              >
                <Text
                  flex={1}
                  fontSize="sm"
                  noOfLines={1}
                  color={colorMode === 'dark' ? 'stone.200' : 'stone.700'}
                  fontStyle={draft.content.trim() ? 'normal' : 'italic'}
                >
                  {draft.filename}
                </Text>
              </Tooltip>
            )}
            {/* Share button - always visible */}
            {renamingDraftId !== draft.id && (
              <Tooltip label="Share">
                <IconButton
                  aria-label="Share draft"
                  icon={<Icon as={Share2} boxSize={3} />}
                  variant="ghost"
                  size="xs"
                  colorScheme="brand"
                  color={
                    draft.content.trim()
                      ? colorMode === 'dark'
                        ? 'brand.400'
                        : 'brand.600'
                      : undefined
                  }
                  onClick={(e) => {
                    e.stopPropagation()
                    onSelectDraft(draft.id)
                    onShareDraft?.()
                  }}
                  isDisabled={!draft.content.trim()}
                  flexShrink={0}
                />
              </Tooltip>
            )}
          </HStack>
        ))}
      </VStack>
    </Box>
  )
}
